/**
 * Must stay accurate to what the app actually does — see
 * netlify/functions/send.ts and netlify/lib/sergeant-chill.ts. This is
 * disclosure, not marketing copy; if the code changes what's collected or
 * why, this file has to change with it.
 */
export function PrivacyPolicy({ onNavigate }: { onNavigate: (path: string) => void }) {
  return (
    <article className="legal">
      <a
        href="/"
        className="back"
        onClick={(e) => {
          e.preventDefault()
          onNavigate('/')
        }}
      >
        ← Back to Mythzord
      </a>
      <h1>Privacy Policy</h1>
      <p className="updated">Last updated 2026-07-25</p>

      <p>
        Mythzord is a fictional chat platform from a television show — a parody, not a real
        Discord-style service. Unlike some other portals in this network, Mythzord has no account
        system and stores nothing you send. This policy is short because there isn't much to tell.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>The message you type.</strong> It is transmitted to our backend (Atlas) and, from
          there, to Anthropic's Claude API, solely to generate Sergeant Chill's automated mute
          response. It is not written to any database, not logged, and not kept after that one
          response is generated.
        </li>
        <li>
          <strong>No accounts, no cookies, no analytics.</strong> There's nothing to sign in to, so
          there's no account data, no session, and no tracking cookie. We don't use third-party
          advertising or analytics trackers.
        </li>
        <li>
          Our hosting and network providers (Netlify, Cloudflare) may briefly retain standard
          technical logs — such as IP address and request metadata — as part of running the
          infrastructure itself. We don't access or use these for anything beyond security and
          keeping the site working.
        </li>
      </ul>

      <h2>Why we collect it</h2>
      <p>
        Only to generate the automatic mute response you see (see the AI Policy in our{' '}
        <button className="link" onClick={() => onNavigate('/terms')}>
          Terms
        </button>
        ) and to operate and secure the site. Nothing you send is reviewed by a person, featured
        anywhere, or used for any other purpose — there's no mechanism for that, because nothing is
        retained past the single request that generates the response.
      </p>

      <h2>Who we share it with</h2>
      <p>
        Two processors, both acting on our behalf and under contract, and only for the single
        request that produces Sergeant Chill's response — never sold, never handed to advertisers:
      </p>
      <ul>
        <li>
          <strong>Atlas</strong> (our own backend) — relays your message to generate the response.
          Atlas does not have an account of yours to attach it to; there is none.
        </li>
        <li>
          <strong>Anthropic</strong> — we send your message text to Anthropic's Claude API, through
          Atlas, to generate the mute verdict. See the AI Policy for detail.
        </li>
      </ul>

      <h2>How long we keep it</h2>
      <p>
        We don't. There is no message history and no account, so there is nothing to request
        access to or deletion of. If you have a question about this anyway, email{' '}
        <a href="mailto:legal@wogd.com">legal@wogd.com</a>.
      </p>

      <h2>Children</h2>
      <p>
        Mythzord is not directed at, and has no signup for, anyone under 13. We do not knowingly
        collect information from anyone under 13 — and, as above, we don't retain message content
        from anyone at all.
      </p>

      <h2>Changes</h2>
      <p>We may update this policy as the product changes. The current version always lives here.</p>

      <h2>Contact</h2>
      <p>
        Questions: <a href="mailto:legal@wogd.com">legal@wogd.com</a>.
      </p>
    </article>
  )
}
